import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const appointmentCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'Appointment',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.appointments.`,
});

// Deduplicate concurrent requests for the same DL ID set
const _inFlight = new Map();

const CHUNK_SIZE = 150;

// Every current caller requests either limit 1 (detail views) or limit 2 (dashboard V-1 comparison).
// Caching this many per DL lets either caller's request be served from the same cache entries.
const PER_DL_LATEST_LIMIT = 2;

// Once the latest-with-statements dataset has been synced at least once, treat the cache as
// authoritative for this window: within it, a fully-cached online read is served straight from
// cache with NO background revalidation, so a dashboard reload — e.g. every time the Back-Office
// re-embeds the WebApp — no longer re-fires the whole per-DL batch against the API. The cache is
// still invalidated eagerly on AppointmentUpdated (iframeWatcher.js → refreshAppointments), so
// freshly created/modified appointments show immediately regardless of this window.
const LATEST_WITH_STATEMENTS_TTL = 30 * 60 * 1000;
const LATEST_WITH_STATEMENTS_SYNCED_AT_KEY = 'latestWithStatements.syncedAt';

async function _getLatestWithStatementsSyncedAt() {
	try {
		const ts = await appointmentCache.get(LATEST_WITH_STATEMENTS_SYNCED_AT_KEY);
		return typeof ts === 'number' ? ts : null;
	} catch {
		return null;
	}
}

async function _markLatestWithStatementsSynced() {
	try {
		await appointmentCache.put(LATEST_WITH_STATEMENTS_SYNCED_AT_KEY, Date.now());
	} catch {}
}

function _chunk(arr, size) {
	const out = [];
	for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
	return out;
}

function _dlLatestWithStatementsKey(deliveryLocationId) {
	return `latestWithStatements.dl.${deliveryLocationId}`;
}

// ─── Latest appointments with statements (key endpoint for KPI) ──────────────

/**
 * Retrieve the latest appointments with statements for the given delivery location IDs.
 *
 * Cached per delivery location (not per requested ID combination): any DL previously fetched in
 * ANY request (dashboard, detail view, list) stays available offline for every other request that
 * includes it, and an offline read returns whatever DLs are cached instead of failing the whole
 * batch when a single DL was never queried in that exact combination before.
 * @param {number[]} deliveryLocationIds
 * @param {{ limit?: number, modifiedAfter?: string|null }} options
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getLatestAppointmentsWithStatements(deliveryLocationIds = [], { limit = 1, modifiedAfter = null } = {}, forceRefresh = false) {
	const sortedIds = [...deliveryLocationIds].sort((a, b) => a - b);

	// Delta-sync callers (none today) keep the previous combined-entry behavior — delta results
	// aren't "the latest N", so they can't be sliced/merged the same way as a full per-DL cache.
	if (modifiedAfter) {
		return _getLatestWithStatementsDelta(sortedIds, limit, modifiedAfter, forceRefresh);
	}

	const memKey = `latestWithStatements.${limit}.${sortedIds.join(',')}`;
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory(memKey);
		if (memCached) return memCached;
	}

	if (_inFlight.has(memKey)) return _inFlight.get(memKey);

	const promise = (async () => {
		try {
			const dlKeys = sortedIds.map(_dlLatestWithStatementsKey);
			const cachedByKey = forceRefresh ? new Map() : await appointmentCache.getMany(dlKeys);

			const foundByDlId = new Map();
			const missingIds = [];
			sortedIds.forEach((dlId, i) => {
				const cached = cachedByKey.get(dlKeys[i]);
				if (cached) foundByDlId.set(dlId, cached);
				else missingIds.push(dlId);
			});

			if (!iframeDataBridge.isOnline()) {
				if (foundByDlId.size === 0) {
					log('warn', 'Appointment', '📴 Offline: no cached latest appointments with statements available');
					return [];
				}
				if (missingIds.length > 0) {
					log('warn', 'Appointment', `📴 Offline: returning partial cached latest appointments with statements (${missingIds.length} delivery location(s) never cached)`);
				}
				const merged = _buildLatestWithStatementsResult(foundByDlId, limit);
				appointmentCache.setInMemory(memKey, merged);
				return merged;
			}

			if (missingIds.length > 0 || forceRefresh) {
				const idsToFetch = forceRefresh ? sortedIds : missingIds;
				const fetchedByDlId = await _fetchAndCacheLatestWithStatements(idsToFetch);
				fetchedByDlId.forEach((data, dlId) => foundByDlId.set(dlId, data));
			} else {
				// Fully served from cache while online. Only revalidate in the background once the
				// dataset has aged past the freshness window (stale-while-revalidate); within the
				// window we skip the refresh so repeated dashboard mounts don't re-fire the batch.
				const syncedAt = await _getLatestWithStatementsSyncedAt();
				const isStale = syncedAt === null || Date.now() - syncedAt > LATEST_WITH_STATEMENTS_TTL;
				if (isStale) {
					setTimeout(() => _refreshLatestWithStatementsInBackground(sortedIds), 100);
				}
			}

			const merged = _buildLatestWithStatementsResult(foundByDlId, limit);
			appointmentCache.setInMemory(memKey, merged);
			return merged;
		} finally {
			_inFlight.delete(memKey);
		}
	})();

	_inFlight.set(memKey, promise);
	return promise;
}

function _buildLatestWithStatementsResult(foundByDlId, limit) {
	const result = [];
	foundByDlId.forEach((appointments) => result.push(...appointments.slice(0, limit)));
	return result;
}

/**
 * Fetches the latest PER_DL_LATEST_LIMIT appointments-with-statements for the given DL IDs and
 * writes each DL's result to its own cache entry (including an explicit empty array for a DL the
 * API returns nothing for, so "no appointment" is distinguished from "never fetched").
 * @returns {Promise<Map<number, Object[]>>}
 */
async function _fetchAndCacheLatestWithStatements(deliveryLocationIds) {
	try {
		const chunks = _chunk(deliveryLocationIds, CHUNK_SIZE);
		const results = await Promise.all(
			chunks.map(async (idsChunk) => {
				const params = new URLSearchParams();
				idsChunk.forEach((id) => params.append('ids', id));
				params.append('number', PER_DL_LATEST_LIMIT);

				const response = await authenticatedFetch(`${getApiUrl('Sales/Appointments/LatestWithStatement')}?${params.toString()}`, { method: 'GET' }, 'tokenSales');
				if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
				return response.json();
			})
		);
		const data = results.flat();

		const byDlId = new Map();
		data.forEach((appointment) => {
			if (!byDlId.has(appointment.deliveryLocationId)) byDlId.set(appointment.deliveryLocationId, []);
			byDlId.get(appointment.deliveryLocationId).push(appointment);
		});
		byDlId.forEach((appointments) => appointments.sort((a, b) => new Date(b.startDate || b.createdDate) - new Date(a.startDate || a.createdDate)));
		deliveryLocationIds.forEach((dlId) => {
			if (!byDlId.has(dlId)) byDlId.set(dlId, []);
		});

		await Promise.all([...byDlId.entries()].map(([dlId, appointments]) => appointmentCache.put(_dlLatestWithStatementsKey(dlId), appointments.slice(0, PER_DL_LATEST_LIMIT))));
		await _markLatestWithStatementsSynced();

		log('success', 'Appointment', `✅ Fetched latest appointments with statements for ${deliveryLocationIds.length} delivery location(s) (${chunks.length} chunk${chunks.length > 1 ? 's' : ''})`);
		return byDlId;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch latest appointments with statements: ${error.message}`);
		throw error;
	}
}

async function _refreshLatestWithStatementsInBackground(deliveryLocationIds) {
	try {
		await _fetchAndCacheLatestWithStatements(deliveryLocationIds);
		log('success', 'Appointment', '🔄 Background refresh of latest appointments with statements completed');
	} catch (error) {
		log('warn', 'Appointment', `⚠️ Background refresh failed: ${error.message}`);
	}
}

// ─── Delta-fetch variant (modifiedAfter) — no current caller; kept on the original combined-key cache ──

async function _getLatestWithStatementsDelta(sortedIds, limit, modifiedAfter, forceRefresh) {
	const cacheKey = `latestWithStatements.delta.${limit}.${sortedIds.join(',')}`;

	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory(cacheKey);
		if (memCached) return memCached;
	}

	if (_inFlight.has(cacheKey)) return _inFlight.get(cacheKey);

	const promise = (async () => {
		try {
			const idbCached = await appointmentCache.get(cacheKey);
			if (idbCached && !forceRefresh) {
				appointmentCache.setInMemory(cacheKey, idbCached);
				return idbCached;
			}

			if (!iframeDataBridge.isOnline()) {
				log('warn', 'Appointment', '📴 Offline: no cached delta appointments with statements available');
				return [];
			}

			const chunks = _chunk(sortedIds, CHUNK_SIZE);
			const results = await Promise.all(
				chunks.map(async (idsChunk) => {
					const params = new URLSearchParams();
					idsChunk.forEach((id) => params.append('ids', id));
					params.append('number', limit);
					params.append('modifiedAfter', new Date(modifiedAfter).toISOString());

					const response = await authenticatedFetch(`${getApiUrl('Sales/Appointments/LatestWithStatement')}?${params.toString()}`, { method: 'GET' }, 'tokenSales');
					if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
					return response.json();
				})
			);
			const data = results.flat();
			await appointmentCache.put(cacheKey, data);
			appointmentCache.setInMemory(cacheKey, data);
			return data;
		} finally {
			_inFlight.delete(cacheKey);
		}
	})();

	_inFlight.set(cacheKey, promise);
	return promise;
}

// ─── Latest appointments (no statements) ─────────────────────────────────────

/**
 * Retrieve the latest appointments for the given delivery location IDs.
 * @param {number[]} deliveryLocationIds
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getLatestAppointments(deliveryLocationIds = [], forceRefresh = false) {
	const sortedIds = [...deliveryLocationIds].sort((a, b) => a - b);
	const cacheKey = `latest.${sortedIds.join(',')}`;

	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory(cacheKey);
		if (memCached) return memCached;
	}

	try {
		const idbCached = await appointmentCache.get(cacheKey);
		if (idbCached && !forceRefresh) {
			appointmentCache.setInMemory(cacheKey, idbCached);
			if (!iframeDataBridge.isOnline()) {
				log('info', 'Appointment', '📴 Offline: returning cached latest appointments');
				return idbCached;
			}
			setTimeout(() => _refreshLatestInBackground(sortedIds, cacheKey), 100);
			return idbCached;
		}

		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Appointment', '📴 Offline: no cached latest appointments available');
			return [];
		}

		return await _fetchLatest(sortedIds, cacheKey);
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting latest appointments: ${error.message}`);
		try {
			const fallback = await appointmentCache.get(cacheKey);
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

async function _fetchLatest(deliveryLocationIds, cacheKey) {
	try {
		const chunks = _chunk(deliveryLocationIds, CHUNK_SIZE);
		const results = await Promise.all(
			chunks.map(async (idsChunk) => {
				const params = new URLSearchParams();
				idsChunk.forEach((id) => params.append('ids', id));
				const response = await authenticatedFetch(`${getApiUrl('Sales/Appointments/Latest')}?${params.toString()}`, { method: 'GET' }, 'tokenSales');
				if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
				return response.json();
			})
		);
		const data = results.flat();
		await appointmentCache.put(cacheKey, data);
		appointmentCache.setInMemory(cacheKey, data);
		log('success', 'Appointment', `✅ Fetched ${data.length} latest appointments (${chunks.length} chunk${chunks.length > 1 ? 's' : ''})`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch latest appointments: ${error.message}`);
		throw error;
	}
}

async function _refreshLatestInBackground(deliveryLocationIds, cacheKey) {
	try {
		await _fetchLatest(deliveryLocationIds, cacheKey);
		log('success', 'Appointment', '🔄 Background refresh of latest appointments completed');
	} catch (error) {
		log('warn', 'Appointment', `⚠️ Background refresh failed: ${error.message}`);
	}
}

// ─── Appointments ─────────────────────────────────────────────────────────────

/**
 * Retrieve all appointments.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointments(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointments');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointments');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointments', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(() => _refreshAppointmentsInBackground(), 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];
		return await _fetchAppointments();
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointments: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointments');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

async function _fetchAppointments() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/Appointments'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointments', data);
		appointmentCache.setInMemory('appointments', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointments`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch appointments: ${error.message}`);
		throw error;
	}
}

async function _refreshAppointmentsInBackground() {
	try {
		await _fetchAppointments();
	} catch (error) {
		log('warn', 'Appointment', `⚠️ Background refresh failed: ${error.message}`);
	}
}

/**
 * Retrieve all appointments with statements.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentsWithStatements(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentsWithStatements');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentsWithStatements');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentsWithStatements', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const response = await authenticatedFetch(getApiUrl('Sales/AppointmentsWithStatements'), { method: 'GET' }, 'tokenSales');
					if (!response.ok) return;
					const data = await response.json();
					await appointmentCache.put('appointmentsWithStatements', data);
					appointmentCache.setInMemory('appointmentsWithStatements', data);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentsWithStatements'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentsWithStatements', data);
		appointmentCache.setInMemory('appointmentsWithStatements', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointments with statements`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointments with statements: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentsWithStatements');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

/**
 * Retrieve a single appointment with its statements by UID.
 * @param {string} uid
 * @returns {Promise<Object|null>}
 */
export async function getAppointmentWithStatements(uid) {
	try {
		if (!iframeDataBridge.isOnline()) {
			log('warn', 'Appointment', '📴 Offline: cannot fetch appointment with statements');
			return null;
		}
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/WithStatement/${uid}`), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch appointment with statements (${uid}): ${error.message}`);
		return null;
	}
}

/**
 * Create or modify an appointment.
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function createOrUpdateAppointment(data) {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/Appointments'), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to create/update appointment: ${error.message}`);
		throw error;
	}
}

/**
 * Delete an appointment by UID.
 * @param {string} uid
 * @returns {Promise<boolean>}
 */
export async function deleteAppointment(uid) {
	try {
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/${uid}`), { method: 'DELETE' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return true;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to delete appointment (${uid}): ${error.message}`);
		throw error;
	}
}

/**
 * Delete all appointments of subType "External".
 * @returns {Promise<boolean>}
 */
export async function deleteExternalAppointments() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/Appointments/External'), { method: 'DELETE' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return true;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to delete external appointments: ${error.message}`);
		throw error;
	}
}

/**
 * Process routes for an appointment.
 * @param {string} uid
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function processAppointmentRoutes(uid, data) {
	try {
		const response = await authenticatedFetch(getApiUrl(`Sales/AppointmentsProcessRoutes/${uid}`), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to process appointment routes (${uid}): ${error.message}`);
		throw error;
	}
}

// ─── Geolocations ─────────────────────────────────────────────────────────────

/**
 * Retrieve all appointment geolocations.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentGeos(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentGeos');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentGeos');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentGeos', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/AppointmentGeos'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentGeos', d);
					appointmentCache.setInMemory('appointmentGeos', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentGeos'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentGeos', data);
		appointmentCache.setInMemory('appointmentGeos', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment geolocations`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment geolocations: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentGeos');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

/**
 * Retrieve an appointment geolocation by UID.
 * @param {string} uid
 * @returns {Promise<Object|null>}
 */
export async function getAppointmentGeo(uid) {
	try {
		if (!iframeDataBridge.isOnline()) return null;
		const response = await authenticatedFetch(getApiUrl(`Sales/AppointmentGeos/${uid}`), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch appointment geo (${uid}): ${error.message}`);
		return null;
	}
}

// ─── Metrics ──────────────────────────────────────────────────────────────────

/**
 * Retrieve all appointment metrics.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentMetrics(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentMetrics');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentMetrics');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentMetrics', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/AppointmentMetrices'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentMetrics', d);
					appointmentCache.setInMemory('appointmentMetrics', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentMetrices'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentMetrics', data);
		appointmentCache.setInMemory('appointmentMetrics', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment metrics`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment metrics: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentMetrics');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

// ─── Objectives ───────────────────────────────────────────────────────────────

/**
 * Retrieve all appointment objectives.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentObjectives(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentObjectives');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentObjectives');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentObjectives', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/AppointmentObjectives'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentObjectives', d);
					appointmentCache.setInMemory('appointmentObjectives', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentObjectives'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentObjectives', data);
		appointmentCache.setInMemory('appointmentObjectives', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment objectives`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment objectives: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentObjectives');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

/**
 * Retrieve an appointment objective by UID.
 * @param {string} uid
 * @returns {Promise<Object|null>}
 */
export async function getAppointmentObjective(uid) {
	try {
		if (!iframeDataBridge.isOnline()) return null;
		const response = await authenticatedFetch(getApiUrl(`Sales/AppointmentObjectives/${uid}`), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch appointment objective (${uid}): ${error.message}`);
		return null;
	}
}

/**
 * Create or update an appointment objective.
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function createOrUpdateAppointmentObjective(data) {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentObjectives'), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to create/update appointment objective: ${error.message}`);
		throw error;
	}
}

// ─── Logs ─────────────────────────────────────────────────────────────────────

/**
 * Retrieve appointment log entries.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentLogs(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentLogs');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentLogs');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentLogs', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/Appointments/Log'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentLogs', d);
					appointmentCache.setInMemory('appointmentLogs', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/Appointments/Log'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentLogs', data);
		appointmentCache.setInMemory('appointmentLogs', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment log entries`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment logs: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentLogs');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

/**
 * Retrieve a single appointment log entry by ID.
 * @param {number} id
 * @returns {Promise<Object|null>}
 */
export async function getAppointmentLog(id) {
	try {
		if (!iframeDataBridge.isOnline()) return null;
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/Log/${id}`), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to fetch appointment log (${id}): ${error.message}`);
		return null;
	}
}

/**
 * Create an appointment log entry.
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function createAppointmentLog(data) {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/Appointments/Log'), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to create appointment log: ${error.message}`);
		throw error;
	}
}

// ─── Comments & Offers ────────────────────────────────────────────────────────

/**
 * Modify an appointment comment.
 * @param {string} uid
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function updateAppointmentComment(uid, data) {
	try {
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/Comment/${uid}`), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to update appointment comment (${uid}): ${error.message}`);
		throw error;
	}
}

/**
 * Accept an appointment offer.
 * @param {string} uid
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function acceptAppointmentOffer(uid, data) {
	try {
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/${uid}/Offers/Accept`), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to accept appointment offer (${uid}): ${error.message}`);
		throw error;
	}
}

/**
 * Refuse an appointment offer.
 * @param {string} uid
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function refuseAppointmentOffer(uid, data) {
	try {
		const response = await authenticatedFetch(getApiUrl(`Sales/Appointments/${uid}/Offers/Refuse`), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to refuse appointment offer (${uid}): ${error.message}`);
		throw error;
	}
}

// ─── Validation log ───────────────────────────────────────────────────────────

/**
 * Retrieve appointment validation log entries.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentValidationLogs(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentValidationLogs');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentValidationLogs');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentValidationLogs', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/Appointments/Validation/Log'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentValidationLogs', d);
					appointmentCache.setInMemory('appointmentValidationLogs', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/Appointments/Validation/Log'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentValidationLogs', data);
		appointmentCache.setInMemory('appointmentValidationLogs', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment validation log entries`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment validation logs: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentValidationLogs');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

// ─── Sharing ──────────────────────────────────────────────────────────────────

/**
 * Retrieve all appointment sharing items.
 * @param {boolean} forceRefresh
 * @returns {Promise<Object[]>}
 */
export async function getAppointmentsSharing(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = appointmentCache.getFromMemory('appointmentsSharing');
		if (memCached) return memCached;
	}

	try {
		const cached = await appointmentCache.get('appointmentsSharing');
		if (cached && !forceRefresh) {
			appointmentCache.setInMemory('appointmentsSharing', cached);
			if (!iframeDataBridge.isOnline()) return cached;
			setTimeout(async () => {
				try {
					const r = await authenticatedFetch(getApiUrl('Sales/AppointmentsSharing'), { method: 'GET' }, 'tokenSales');
					if (!r.ok) return;
					const d = await r.json();
					await appointmentCache.put('appointmentsSharing', d);
					appointmentCache.setInMemory('appointmentsSharing', d);
				} catch {}
			}, 100);
			return cached;
		}

		if (!iframeDataBridge.isOnline()) return [];

		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentsSharing'), { method: 'GET' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		const data = await response.json();
		await appointmentCache.put('appointmentsSharing', data);
		appointmentCache.setInMemory('appointmentsSharing', data);
		log('success', 'Appointment', `✅ Fetched ${data.length} appointment sharing items`);
		return data;
	} catch (error) {
		log('error', 'Appointment', `❌ Error getting appointment sharing: ${error.message}`);
		try {
			const fallback = await appointmentCache.get('appointmentsSharing');
			if (fallback) return fallback;
		} catch {}
		return [];
	}
}

/**
 * Create or update appointment sharing items.
 * @param {Object} data
 * @returns {Promise<Object|null>}
 */
export async function createOrUpdateAppointmentsSharing(data) {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentsSharing'), { method: 'POST', body: JSON.stringify(data) }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return await response.json();
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to create/update appointment sharing: ${error.message}`);
		throw error;
	}
}

/**
 * Delete an appointment sharing item.
 * @returns {Promise<boolean>}
 */
export async function deleteAppointmentsSharing() {
	try {
		const response = await authenticatedFetch(getApiUrl('Sales/AppointmentsSharing'), { method: 'DELETE' }, 'tokenSales');
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		return true;
	} catch (error) {
		log('error', 'Appointment', `❌ Failed to delete appointment sharing: ${error.message}`);
		throw error;
	}
}

// ─── Cache management ─────────────────────────────────────────────────────────

/**
 * Invalidate appointment caches.
 *
 * With a delivery location id (e.g. from AppointmentUpdated after a visit close), only that DL's
 * persisted latest-with-statements entry is dropped so just it re-fetches from network; the rest of
 * the park stays in IndexedDB and is served from there on the next KPI recompute — avoiding a
 * full-park network re-materialization on every visit close (RAM spike, #23222). The (bounded)
 * in-memory layer is always cleared so aggregated entries can't return stale data for the changed
 * DL; they are rebuilt from IndexedDB on next read. With no id, the whole cache is cleared.
 * @param {number|null} deliveryLocationId
 */
export async function refreshAppointments(deliveryLocationId = null) {
	appointmentCache.clearMemory();
	if (deliveryLocationId != null) {
		try {
			await appointmentCache.delete(_dlLatestWithStatementsKey(deliveryLocationId));
		} catch (error) {
			log('warn', 'Appointment', `Scoped appointment cache invalidation failed for DL ${deliveryLocationId}: ${error.message}`);
		}
		_inFlight.clear();
		return;
	}
	await appointmentCache.clear();
	_inFlight.clear();
}
